import FetchError from '../FetchError'
import { classifyError } from './userErrorUtils'
import type {
  InspectorCandidateAnchor,
  InspectorCandidateTier,
  RankingCompleteness,
  SampleInspectorCandidate
} from '../types/types'

export function formatCandidateLabel(candidate: {
  userId: string
  name?: string
  email?: string
}): string {
  const trimmedName = candidate.name?.trim()
  const trimmedEmail = candidate.email?.trim()

  if (trimmedName && trimmedName.length > 0) {
    if (trimmedEmail && trimmedEmail.length > 0) {
      return `${trimmedName} (${trimmedEmail})`
    }
    return trimmedName
  }

  if (trimmedEmail && trimmedEmail.length > 0) {
    return trimmedEmail
  }

  if (
    candidate.userId.startsWith('client:') ||
    candidate.userId.startsWith('maskinporten:')
  ) {
    return `Maskinbruker (${candidate.userId})`
  }

  return `Bruker uten navn (${candidate.userId})`
}

export function formatTierLabel(
  tier?: InspectorCandidateTier | string
): string {
  switch (tier) {
    case 'TEAM':
      return 'Team'
    case 'SECTION':
      return 'Seksjon'
    case 'DEPARTMENT':
      return 'Avdeling'
    case 'REGION':
      return 'Region'
    case 'OTHER':
      return 'Annet'
    default:
      return tier ?? 'Ukjent'
  }
}

export function formatAnchorTypeLabel(type?: string): string {
  switch (type) {
    case 'TEAM':
      return 'Team'
    case 'SEKSJON':
      return 'Seksjon'
    case 'AVDELING':
      return 'Avdeling'
    case 'REGION':
      return 'Region'
    default:
      return type ?? ''
  }
}

export function formatAnchorLabel(
  anchor?: InspectorCandidateAnchor | null
): string {
  if (!anchor) return 'Ingen orgenhet tilknyttet'
  const typeLabel = formatAnchorTypeLabel(anchor.type)
  const displayName = anchor.name?.trim() || anchor.id
  return typeLabel ? `${displayName} (${typeLabel})` : displayName
}

export function getRankingCompletenessMessage(
  completeness?: RankingCompleteness
): string | null {
  switch (completeness) {
    case 'COMPLETE':
      return null
    case 'PARTIAL':
      return 'Delvis rangering av kandidater. Enkelte organisasjonsenheter kunne ikke hentes inn.'
    case 'DEGRADED':
      return 'Begrenset rangering. Organisasjonstjenesten er midlertidig utilgjengelig eller ufullstendig.'
    case 'UNAVAILABLE':
      return 'Organisasjonsinformasjon er utilgjengelig. Viser alle tilgjengelige brukere uten full rangering.'
    default:
      return null
  }
}

export function isCandidateCurrentInspector(
  candidate: SampleInspectorCandidate,
  currentInspectorUserId?: string
): boolean {
  if (candidate.currentInspector) return true
  if (currentInspectorUserId && candidate.userId === currentInspectorUserId) {
    return true
  }
  return false
}

/**
 * Resolves current inspector display using sample.userId as the authoritative version snapshot.
 * Looks up the candidate list for name/tier, falling back to candidateCurrentInspector or identity.
 */
export function resolveCurrentInspectorDisplay(
  authoritativeUserId?: string | null,
  candidates?: SampleInspectorCandidate[],
  candidateCurrentInspector?: SampleInspectorCandidate | null
): string {
  if (!authoritativeUserId) {
    return 'Ingen inspektør tildelt'
  }

  const matchingCandidate = candidates?.find(
    (c) => c.userId === authoritativeUserId
  )
  if (matchingCandidate) {
    const label = formatCandidateLabel(matchingCandidate)
    const tier = formatTierLabel(matchingCandidate.tier)
    return `${label} (${tier})`
  }

  if (candidateCurrentInspector?.userId === authoritativeUserId) {
    const label = formatCandidateLabel(candidateCurrentInspector)
    const tier = formatTierLabel(candidateCurrentInspector.tier)
    return `${label} (${tier})`
  }

  return formatCandidateLabel({ userId: authoritativeUserId })
}

export function translateInspectorError(error: unknown): string {
  const { kind, status } = classifyError(error)

  switch (status) {
    case 400:
      return 'Ugyldig forespørsel ved oppdatering av inspektør. Kontroller opplysningene og prøv igjen.'
    case 403:
      return 'Handlingen krever administratorrettigheter.'
    case 404:
      return 'Prøven eller den valgte brukeren ble ikke funnet.'
    case 412:
      return 'Prøven har blitt endret av en annen bruker eller i en annen økt. Last siden på nytt og prøv igjen.'
    default:
      if (kind === 'network') {
        return 'Nettverksfeil. Vennligst sjekk tilkoblingen og prøv igjen.'
      }
      return 'Kunne ikke oppdatere inspektør. Vennligst prøv igjen.'
  }
}

export function getSampleLookupErrorMessage(
  error: unknown,
  sampleId: string
): { title: string; message: string } {
  const status = error instanceof FetchError ? error.status : undefined

  if (status === 404) {
    return {
      title: 'Fant ikke prøve',
      message: `Kunne ikke finne prøve med ID «${sampleId}». Kontroller at prøve-ID-en er riktig.`
    }
  }

  if (status === 403) {
    return {
      title: 'Ingen tilgang',
      message: 'Du har ikke tilgang til å se eller redigere denne prøven.'
    }
  }

  return {
    title: 'Feil ved henting av prøve',
    message: 'Det oppstod en feil ved henting av prøven. Vennligst prøv igjen.'
  }
}
